import { FunctionComponent, useCallback, useEffect, useState } from 'react';

import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { AccountModel, LocationModel, UserModel, WorklistViewAccessListModel, WorklistViewAccessModel, WorklistViewModel } from 'models';

import { FieldContainer, GridColumn, createStyledRhfForm } from 'core/forms';
import { RHF_FULL_RESET } from 'core/forms/constants';
import { useEvent, useValidatedParam } from 'core/hooks';
import { NotificationsService } from 'core/notifications';
import { Button, ButtonVariants, DropdownField, Label, MultiSelectChangeEvent, Page, PageHeader, SwitchField } from 'core/ui';

import { AccountMultiSelect } from 'features/account/fragments/AccountMultiSelect';

import { LocationMultiSelect } from '../../location';
import { UserMultiSelect } from '../../user';
import { WorklistViewUserEditService } from '../services';
import { EditFormValues } from '../types';
import { WorklistViewUserFormSkeleton } from './WorklistViewUserFormSkeleton';

export const WorklistViewUserForm: FunctionComponent = () => {
  const id = useValidatedParam('id', 'integer', false);
  const navigate = useNavigate();

  const [accessModel, setAccessModel] = useState<WorklistViewAccessListModel | null>(null);
  const [allUsers, setAllUsers] = useState<UserModel[] | null>(null);
  const [allAccessModes, setAllAccessModes] = useState<WorklistViewAccessModel[] | null>(null);
  const [allWorklists, setAllWorklists] = useState<WorklistViewModel[] | null>(null);
  const [selectedLocations, setSelectedLocations] = useState<LocationModel[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<UserModel[]>([]);
  const [selectedAccounts, setSelectedAccounts] = useState<AccountModel[]>([]);

  const [locations, setLocations] = useState<LocationModel[]>([]);
  const [accounts, setAccounts] = useState<AccountModel[]>([]);

  // Enhanced loading states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loadingSteps, setLoadingSteps] = useState<string[]>([]);

  const isInitializing = (accessModel == null && id != null) || allUsers == null || allAccessModes == null || allWorklists == null;
  const isEditMode = id != null;

  const rhfContext = useForm<EditFormValues>({
    defaultValues: {
      worklist: null,
      users: [],
      access: null,
      active: false,
      default: false,
      locations: [],
      accounts: [],
    },
  });
  const { reset } = rhfContext;

  const handleSubmit: SubmitHandler<EditFormValues> = useCallback(
    async (data, event) => {
      if (isSubmitting) return; // Prevent double submission

      try {
        setIsSubmitting(true);
        const newModel = await WorklistViewUserEditService.save(data, accessModel);
        setAccessModel(newModel);
        NotificationsService.displaySuccess('Worklist access saved');
        navigate('/worklist-view-user');
      } catch (error) {
        console.error('Error saving worklist access:', error);
        NotificationsService.displayError('Failed to save worklist access');
      } finally {
        setIsSubmitting(false);
      }
    },
    [accessModel, navigate, isSubmitting],
  );

  const handleDeleteClick = useCallback(async () => {
    if (accessModel == null) {
      throw new Error('Cannot delete access because the model is null or undefined.');
    }

    if (isDeleting) return; // Prevent double deletion

    try {
      setIsDeleting(true);
      await WorklistViewUserEditService.deleteAccess(accessModel.id);
      NotificationsService.displaySuccess('Worklist access removed');
      navigate('/worklist-view-user');
    } catch (error) {
      console.error('Error deleting worklist access:', error);
      NotificationsService.displayError('Failed to delete worklist access');
    } finally {
      setIsDeleting(false);
    }
  }, [accessModel, navigate, isDeleting]);

  useEffect(() => {
    (async () => {
      try {
        // Track loading progress for better UX
        setLoadingSteps(['Initializing...']);

        setLoadingSteps((prev) => [...prev, 'Loading worklist data...']);
        const results = await WorklistViewUserEditService.initialize(id);

        setLoadingSteps((prev) => [...prev, 'Processing user data...']);
        setAccessModel(results.model);
        setAllUsers(results.allUsers);
        setAllAccessModes(results.allWorklistAccessType);
        setAllWorklists(results.allWorklists);

        setLoadingSteps((prev) => [...prev, 'Loading locations and accounts...']);
        setLocations(results.allLocations);
        setAccounts(results.allAccounts);

        setLoadingSteps((prev) => [...prev, 'Setting up form...']);
        reset(results.form, RHF_FULL_RESET);

        setLoadingSteps((prev) => [...prev, 'Complete!']);
      } catch (error) {
        console.error('Error initializing form:', error);
        NotificationsService.displayError('Failed to load form data');
        setLoadingSteps((prev) => [...prev, 'Error loading data']);
      }
    })();
  }, [id, reset]);

  const handleFormLocationChange = useEvent((data: MultiSelectChangeEvent) => {
    if (data) {
      const selectedValues = data.value as LocationModel[];

      if (selectedValues) {
        setSelectedLocations(selectedValues);
      }
    }
  });

  const handleFormUserChange = useEvent((data: MultiSelectChangeEvent) => {
    if (data) {
      const selectedValues = data.value as UserModel[];

      if (selectedValues) {
        setSelectedUsers(selectedValues);
      }
    }
  });

  const handleFormAccountChange = useEvent((data: MultiSelectChangeEvent) => {
    if (data) {
      const selectedValues = data.value as AccountModel[];

      if (selectedValues) {
        setSelectedAccounts(selectedValues);
      }
    }
  });

  // Show skeleton loader during initialization
  if (isInitializing) {
    const currentLoadingMessage = loadingSteps.length > 0 ? loadingSteps[loadingSteps.length - 1] : 'Loading form data...';

    return <WorklistViewUserFormSkeleton loadingMessage={currentLoadingMessage} isEditMode={isEditMode} />;
  }

  return (
    <FormProvider {...rhfContext}>
      <StyledPage>
        <PageHeader title={id == null ? 'New Worklist Access Rule' : 'Edit Worklist Access Rule'} />
        <StyledForm autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck="false" noValidate onSubmit={rhfContext.handleSubmit(handleSubmit)}>
          <GridColumn columnStart="1" isLabelColumn>
            <Label required>Worklist</Label>
          </GridColumn>
          <GridColumn columnStart="2">
            <FieldContainer $hideLabel>
              <DropdownField id="worklist" name="worklist" data={allWorklists} dataItemKey="id" textField="name" required />
            </FieldContainer>
          </GridColumn>

          <GridColumn columnStart="1" isLabelColumn>
            <Label required>Access</Label>
          </GridColumn>
          <GridColumn columnStart="2">
            <FieldContainer $hideLabel>
              <DropdownField name="access" data={allAccessModes} dataItemKey="id" textField="name" required filterable={false} />
            </FieldContainer>
          </GridColumn>

          <GridColumn columnStart="1" isLabelColumn>
            <Label>Default</Label>
          </GridColumn>
          <GridColumn columnStart="2">
            <FieldContainer $hideLabel>
              <SwitchField name="default" />
            </FieldContainer>
          </GridColumn>

          <GridColumn columnStart="1" isLabelColumn>
            <Label>Active</Label>
          </GridColumn>
          <GridColumn columnStart="2">
            <FieldContainer $hideLabel>
              <SwitchField name="active" />
            </FieldContainer>
          </GridColumn>

          <GridColumn columnStart="1" isLabelColumn>
            <Label required>User</Label>
          </GridColumn>
          <GridColumn columnStart="2">
            <FieldContainer $hideLabel>
              <UserMultiSelect
                name="users"
                onChange={handleFormUserChange}
                data={allUsers || []}
                required
                filterable={true}
                enableVirtualization={true}
                minSearchLength={3}
                pageSize={80}
                itemHeight={28}
                debounceDelay={300}
                requiredMessage="Please select at least one user"
              />
            </FieldContainer>
          </GridColumn>

          <GridColumn columnStart="1" isLabelColumn>
            <Label>Location</Label>
          </GridColumn>
          <GridColumn columnStart="2">
            <FieldContainer $hideLabel>
              <LocationMultiSelect
                data={locations}
                name="locations"
                id="locations"
                onChange={handleFormLocationChange}
                filterable={true}
                enableVirtualization={true}
                minSearchLength={3}
                pageSize={80}
                itemHeight={28}
                debounceDelay={300}
                filterFields={['name', 'code']}
              />
            </FieldContainer>
          </GridColumn>

          <GridColumn columnStart="1" isLabelColumn>
            <Label>Account</Label>
          </GridColumn>
          <GridColumn columnStart="2">
            <FieldContainer $hideLabel>
              <AccountMultiSelect data={accounts} name="accounts" id="accounts" onChange={handleFormAccountChange} filterable={true} />
            </FieldContainer>
          </GridColumn>
          <StyledActionsColumn columnStart="1" columnEnd="span 2">
            <Button variant={ButtonVariants.SECONDARY} disabled={accessModel == null || id == null || isDeleting || isSubmitting} onClick={handleDeleteClick}>
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
            <Button type="submit" disabled={isSubmitting || isDeleting}>
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </StyledActionsColumn>
        </StyledForm>
      </StyledPage>
    </FormProvider>
  );
};

WorklistViewUserForm.displayName = 'WorklistViewUserForm';

const StyledForm = createStyledRhfForm('min-content 400px min-content');

const StyledActionsColumn = styled(GridColumn)`
  display: flex;
  justify-content: space-between;
`;

const StyledPage = styled(Page)`
  font-size: 14px;

  .k-chip-content {
    font-size: 14px;
  }
`;
